import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookMarked, Flame, MessageCircle, Music, Sparkles, Timer, Trash2, Plus } from "lucide-react";

const Journal = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);
  const firstRun = useRef(true);

  const reload = () => {
    return api.get("/me/journal/today").then(({ data }) => {
      setData(data);
      setSavedAt(data.note_updated_at);
    });
  };

  useEffect(() => {
    reload().catch(() => toast.error("Could not load your journal"));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (data && note === "" && data.note) {
      setNote(data.note);
    }
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.post("/me/journal", { text: note });
        setSavedAt(data.updated_at);
      } catch {
        toast.error("Couldn't save journal entry");
      } finally {
        setSaving(false);
      }
    }, 1000);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line
  }, [note]);

  if (!data) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;

  const dateFmt = new Date(data.entry_date + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });

  return (
    <div className="fade-in max-w-4xl mx-auto px-6 py-16">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">Recovery Journal</div>
        <h1 className="font-display text-5xl md:text-6xl leading-tight">
          <span className="text-neutral-100">Today, </span>
          <span className="gradient-text">{dateFmt.split(",")[0].toLowerCase()}.</span>
        </h1>
        <p className="mt-4 text-neutral-400">{dateFmt}</p>
      </div>

      {/* Streak line */}
      <div className="card-surface p-5 mb-8 flex items-center gap-4" data-testid="journal-streak">
        <Flame className="text-teal-400" />
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Pivot Streak</div>
          <div className="font-display text-2xl">{data.streak} {data.streak === 1 ? "day" : "days"}</div>
        </div>
      </div>

      {/* Free-form note */}
      <div className="card-surface p-6 mb-8" data-testid="journal-note-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookMarked size={16} className="text-teal-400" />
            <h2 className="font-display text-xl">A page for today</h2>
          </div>
          <span className="text-xs text-neutral-500" data-testid="journal-note-status">
            {saving ? "Saving…" : savedAt ? "Saved" : note ? "Unsaved…" : "Private to you"}
          </span>
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything you want to write. Nobody sees this but you."
          rows={6}
          data-testid="journal-note-input"
          className="wwr-input w-full rounded-xl px-4 py-3 text-sm resize-none"
        />
      </div>

      {/* Today's activity */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Section title="Today's pivots" icon={<Timer size={16} className="text-teal-400" />} testid="journal-pivots">
          {data.pivots.length === 0 ? (
            <Empty text="No 60-second pivots yet today." />
          ) : (
            <ul className="space-y-2">
              {data.pivots.map((p) => (
                <li key={p.id} className="text-sm text-neutral-300 flex justify-between">
                  <span>{p.duration_seconds}s</span>
                  <span className="text-neutral-500">{new Date(p.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title="Today's missions" icon={<Sparkles size={16} className="text-teal-400" />} testid="journal-missions">
          {data.missions.length === 0 ? (
            <Empty text={<>No missions yet — <Link to="/studio" className="text-teal-300">generate one</Link>.</>} />
          ) : (
            <ul className="space-y-3">
              {data.missions.map((m) => (
                <li key={m.id} className="text-sm text-neutral-200 leading-relaxed">
                  <span className="text-xs text-neutral-500 block mb-1">mood: {m.mood}</span>
                  {m.mission}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div className="grid md:grid-cols-1 gap-4 mb-8">
        <Section title="Today's reframes" icon={<MessageCircle size={16} className="text-teal-400" />} testid="journal-reframes">
          {data.reframes.length === 0 ? (
            <Empty text={<>Nothing to reframe yet — <Link to="/studio" className="text-teal-300">try the Reframer</Link>.</>} />
          ) : (
            <ul className="space-y-4">
              {data.reframes.map((r) => (
                <li key={r.id} className="text-sm">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">The mess</div>
                  <p className="text-neutral-400 mb-2">{r.mess}</p>
                  <div className="text-xs uppercase tracking-widest text-teal-300/80 mb-1">The message</div>
                  <p className="text-neutral-100 leading-relaxed">{r.message}</p>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Now Playing */}
      <div className="mb-8">
        <NowPlaying entries={data.listening || []} onRefresh={reload} />
      </div>

      {/* Reflections from today */}
      <Section title="Toolkit reflections you wrote today" icon={<BookMarked size={16} className="text-teal-400" />} testid="journal-reflections">
        {data.reflections.length === 0 ? (
          <Empty text={<>Nothing from the toolkits today. <Link to="/dashboard" className="text-teal-300">Open one</Link>.</>} />
        ) : (
          <ul className="space-y-4">
            {data.reflections.map((r) => (
              <li key={r.toolkit_id + "-" + r.section_index} className="text-sm">
                <div className="text-xs text-neutral-500 mb-1">
                  <span className="text-teal-300/80">{r.toolkit_title}</span> · {r.section_title}
                </div>
                <p className="text-neutral-200 leading-relaxed whitespace-pre-line">{r.text}</p>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
};

const Section = ({ title, icon, testid, children }) => (
  <div className="card-surface p-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      {icon}
      <h2 className="font-display text-lg">{title}</h2>
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <div className="text-sm text-neutral-500">{text}</div>;

const NowPlaying = ({ entries, onRefresh }) => {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!title.trim() || !artist.trim()) {
      toast.error("Song title and artist are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/me/listening", {
        title: title.trim(),
        artist: artist.trim(),
        note: note.trim() || null,
      });
      setTitle(""); setArtist(""); setNote("");
      await onRefresh();
      toast.success("Logged.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/me/listening/${id}`);
      await onRefresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="card-surface p-6" data-testid="journal-now-playing">
      <div className="flex items-center gap-2 mb-3">
        <Music size={16} className="text-teal-400" />
        <h2 className="font-display text-lg">Now playing</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-4">
        Log the songs you're listening to today — even a single track can be an emotional timestamp.
      </p>
      <div className="grid md:grid-cols-[1fr_1fr_2fr_auto] gap-2 mb-4">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Song title"
          data-testid="np-title"
          className="wwr-input rounded-xl px-3 py-2 text-sm"
        />
        <input
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
          placeholder="Artist"
          data-testid="np-artist"
          className="wwr-input rounded-xl px-3 py-2 text-sm"
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="How it landed (optional)"
          data-testid="np-note"
          className="wwr-input rounded-xl px-3 py-2 text-sm"
        />
        <button
          onClick={add}
          disabled={saving}
          data-testid="np-add-btn"
          className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
        >
          <Plus size={12} /> Log
        </button>
      </div>
      {entries.length === 0 ? (
        <Empty text="Nothing logged yet today." />
      ) : (
        <ul className="space-y-2" data-testid="np-list">
          {entries.map((e) => (
            <li key={e.id} className="flex items-start justify-between gap-3 py-2 border-b border-neutral-800 last:border-0" data-testid={`np-entry-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div>
                  <span className="text-neutral-100">{e.title}</span>
                  <span className="text-neutral-500 text-sm"> — {e.artist}</span>
                </div>
                {e.note && (
                  <p className="text-sm text-neutral-400 italic mt-1">"{e.note}"</p>
                )}
                <div className="text-xs text-neutral-500 mt-1">
                  {new Date(e.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
              <button
                onClick={() => remove(e.id)}
                data-testid={`np-delete-${e.id}`}
                className="text-neutral-500 hover:text-red-400 p-1"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Journal;
